import { ReactNode } from "react";
import clsx from "clsx";
import PDFIcon from "../../assets/img/icn-pdf.svg";
import BlankIcon from "../../assets/img/icn-_blank.svg";
import styles from "./Button.module.css";
import { config } from "../config/Config";

export enum ButtonPriority {
  BACK = "return",
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export enum ButtonWidth {
  NORMAL = "",
  NARROW = "type-narrow",
  WIDE = "type-wide",
  FULL = "type-full",
}

export enum TextSize {
  NORMAL = "",
  SMALL = "type-small",
  LARGE = "type-large",
}

export enum ButtonType {
  BUTTON = "button",
  SUBMIT = "submit",
  RESET = "reset",
  LINK = "link",
}

export enum Align {
  DEFAULT = "",
  LEFT = "align-left",
  RIGHT = "align-right",
}

export enum ButtonClaimType {
  NORMAL = "",
  CLAIM = "type-claim",
}

export interface ActionButtonProps {
  type?: ButtonType;
  priority?: ButtonPriority;
  width?: ButtonWidth;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled?: boolean;
  text: string;
  useIcon?: boolean;
}

export interface LinkButtonProps {
  type?: ButtonType.LINK;
  href?: string;
  priority?: ButtonPriority;
  width?: ButtonWidth;
  target?: string;
  text: string;
  useBlankIcon?: boolean;
  action?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  activeForAgent?: boolean;
}

export type ButtonProps = ActionButtonProps | LinkButtonProps;

export interface ButtonWrapperProps {
  col?: 1 | 2 | 3 | 4 | 5 | 6;
  colPc?: 1 | 2 | 3 | 4 | 5 | 6;
  align?: Align;
  textSize?: TextSize;
  buttons?: ButtonProps[];
  claimtype?: ButtonClaimType;
  children?: ReactNode;
  className?: string;
}

export const ActionButton = (props: ActionButtonProps) => {
  const {
    type = ButtonType.BUTTON,
    priority = ButtonPriority.LOW,
    width = ButtonWidth.NORMAL,
    onClick: handleClick,
    disabled = false,
    text,
    useIcon = false,
  } = props;
  const componentStyle = clsx(styles["btn"], styles[priority], styles[width]);

  return (
    <div className={componentStyle}>
      <button
        type={type !== ButtonType.LINK ? type : ButtonType.BUTTON}
        onClick={handleClick}
        disabled={disabled}
      >
        <span>
          {text}
          {useIcon && (
            <img
              src={PDFIcon}
              className={styles["icn-pdf"]}
              alt="PDFファイル"
              width="12"
              height="12"
            />
          )}
        </span>
      </button>
    </div>
  );
};

export const LinkButton = (props: LinkButtonProps) => {
  const {
    priority = ButtonPriority.LOW,
    width = ButtonWidth.NORMAL,
    text,
    href,
    target,
    useBlankIcon = true,
    action,
    activeForAgent = false,
  } = props;
  const componentStyle = clsx(styles["btn"], styles[priority], styles[width]);

  return (
    <div className={componentStyle}>
      <a
        href={activeForAgent ? href : ""}
        target={target}
        onClick={
          config.agent && !activeForAgent
            ? (e) => {
                e.stopPropagation();
                e.preventDefault();
              }
            : action
        }
      >
        <span>
          {target === "_blank" ? (
            <>
              {useBlankIcon ? (
                <img
                  src={BlankIcon}
                  className={styles["icn-_blank"]}
                  alt={"新しいウィンドウで開く"}
                  width={12}
                  height={10}
                />
              ) : (
                <img
                  src={PDFIcon}
                  className={styles["icn-pdf"]}
                  alt="PDFファイル"
                  width="12"
                  height="12"
                />
              )}
              {text}
            </>
          ) : (
            text
          )}
        </span>
      </a>
    </div>
  );
};

export const ButtonWrapper = (props: ButtonWrapperProps) => {
  const {
    col,
    colPc,
    align = Align.DEFAULT,
    buttons,
    textSize = TextSize.NORMAL,
    claimtype = ButtonClaimType.NORMAL,
    children,
    className,
  } = props;

  const componentStyle = clsx(
    styles["mod-btn"],
    styles[align],
    styles[textSize],
    styles[claimtype],
    className
  );

  return (
    <div className={componentStyle} data-col={col} data-col-pc={colPc}>
      {buttons?.map((button: ButtonProps, index: number) => {
        // If button type is not Link => ActionButton
        if (button.type !== ButtonType.LINK) {
          const props = button as ActionButtonProps;
          return <ActionButton key={index} {...props} />;
        }

        const props = button as LinkButtonProps;
        return <LinkButton key={index} {...props} />;
      })}
      {children}
    </div>
  );
};
